import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import type { PublicPlayer } from '@shared/types';
import { initial, useAccount } from '../pages/account/session';

/** The main sections, shown across the top on wide screens. */
const MAIN_LINKS: { to: string; label: string }[] = [
  { to: '/', label: 'Play' },
  { to: '/games', label: 'Party games' },
  { to: '/packs', label: 'Card packs' },
  { to: '/how-to-play', label: 'How to play' },
  { to: '/shop', label: 'Shop' },
  { to: '/events', label: 'Events' },
];

/** Everything else, grouped. Wide screens show these under "More"; phones get all of it in the menu. */
const MORE_GROUPS: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: 'Games',
    links: [
      { to: '/', label: 'Start a room' },
      { to: '/games/one-phone', label: 'Play on one phone' },
      { to: '/games/charades', label: 'Dumb Charades' },
      { to: '/games/dialogue', label: 'Finish the Dialogue' },
      { to: '/games/mat-bolna', label: 'Mat Bolna' },
    ],
  },
  {
    title: 'Cards',
    links: [
      { to: '/packs', label: 'Card packs' },
      { to: '/packs/new', label: 'Make your own pack' },
      { to: '/suggest', label: 'Suggest a card' },
      { to: '/shop', label: 'Printed decks' },
    ],
  },
  {
    title: 'For the occasion',
    links: [
      { to: '/diwali-party-games', label: 'Diwali' },
      { to: '/sangeet-games', label: 'Sangeet and weddings' },
      { to: '/eid-party-games', label: 'Eid' },
      { to: '/baby-shower-games-desi', label: 'Baby showers' },
      { to: '/family-zoom-games', label: 'Family video calls' },
      { to: '/desi-party-games', label: 'Desi party games' },
      { to: '/events', label: 'Hosting an event' },
    ],
  },
  {
    title: 'Help',
    links: [
      { to: '/how-to-play', label: 'How to play' },
      { to: '/account', label: 'Your account' },
    ],
  },
];

export function TopBar({ children }: { children?: ReactNode }) {
  const [menu, setMenu] = useState<'more' | 'drawer' | null>(null);
  const location = useLocation();
  const moreButton = useRef<HTMLButtonElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Close on navigation, on Escape, and (for the dropdown) on a click outside it.
  useEffect(() => setMenu(null), [location.pathname]);
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenu(null);
      (menu === 'more' ? moreButton : menuButton).current?.focus();
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const onButton = moreButton.current?.contains(target) || menuButton.current?.contains(target);
      if (!panel.current?.contains(target) && !onButton) setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    if (menu === 'drawer') document.body.classList.add('menu-open');
    panel.current?.querySelector<HTMLElement>('a')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      document.body.classList.remove('menu-open');
    };
  }, [menu]);

  return (
    <header className="site-header">
      <div className="topbar">
        <Link to="/" className="brand">
          <img className="logo" src="/logo-mark.png" alt="" />
          CompleteMasti
        </Link>
        <nav className="site-nav" aria-label="Main">
          {MAIN_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === '/'}>
              {l.label}
            </NavLink>
          ))}
          <div className="site-more">
            <button
              type="button"
              className="site-nav-more"
              aria-expanded={menu === 'more'}
              aria-controls="site-more-panel"
              ref={moreButton}
              onClick={() => setMenu(menu === 'more' ? null : 'more')}
            >
              More <span aria-hidden="true">▾</span>
            </button>
            {menu === 'more' && (
              <div id="site-more-panel" className="site-more-panel" ref={panel}>
                <MenuGroups />
              </div>
            )}
          </div>
        </nav>
        <span className="spacer" />
        {children}
        <AccountLink />
        <button
          type="button"
          className="menu-toggle"
          aria-expanded={menu === 'drawer'}
          aria-controls="site-drawer"
          aria-label={menu === 'drawer' ? 'Close menu' : 'Open menu'}
          ref={menuButton}
          onClick={() => setMenu(menu === 'drawer' ? null : 'drawer')}
        >
          <span aria-hidden="true">{menu === 'drawer' ? '✕' : '☰'}</span>
        </button>
      </div>
      {menu === 'drawer' && (
        <div id="site-drawer" className="site-drawer" ref={panel}>
          <nav aria-label="Main">
            <MenuGroups />
          </nav>
        </div>
      )}
    </header>
  );
}

function MenuGroups() {
  return (
    <div className="menu-groups">
      {MORE_GROUPS.map((g) => (
        <section key={g.title}>
          <h2>{g.title}</h2>
          <ul>
            {g.links.map((l) => (
              <li key={l.label}>
                <NavLink to={l.to} end>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** Top bar account button: a round initial when signed in, a plain person icon for guests. */
function AccountLink() {
  const { user } = useAccount();
  if (user) {
    return (
      <Link to="/account" className="account-link signed-in" aria-label={`Your account (${user.displayName})`} title={user.displayName}>
        <span aria-hidden="true">{initial(user.displayName)}</span>
      </Link>
    );
  }
  return (
    <Link to="/account" className="account-link" aria-label="Sign in (optional)" title="Sign in">
      <span aria-hidden="true">👤</span>
    </Link>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <p>
        Made by Priten &amp; Chandani, a brother and sister duo navigating their ABCD life.
      </p>
      <p>
        <Link to="/how-to-play">How to play</Link> · <Link to="/games">Party games</Link> · <Link to="/packs">Card packs</Link> ·{' '}
        <Link to="/shop">Card decks</Link> · <Link to="/events">Events</Link> · <Link to="/suggest">Suggest a card</Link>
      </p>
    </footer>
  );
}

export function Avatar({ player, size }: { player: Pick<PublicPlayer, 'avatar' | 'connected' | 'isHost'>; size?: 'lg' }) {
  return (
    <span className={`avatar ${size ?? ''} ${player.connected ? '' : 'offline'}`} aria-hidden="true">
      {player.avatar}
      {player.isHost && <span className="badge" title="Host">⭐</span>}
    </span>
  );
}

/** Seconds left until a server deadline, ticking once a second. */
export function useCountdown(deadline: number | null, serverNow: () => number): number | null {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!deadline) {
      setLeft(null);
      return;
    }
    const update = () => setLeft(Math.max(0, Math.ceil((deadline - serverNow()) / 1000)));
    update();
    const t = setInterval(update, 250);
    return () => clearInterval(t);
  }, [deadline, serverNow]);
  return left;
}

export function Timer({ seconds }: { seconds: number | null }) {
  if (seconds === null) return null;
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return (
    <span className={`timer ${seconds <= 10 ? 'low' : ''}`} aria-label={`${seconds} seconds left`}>
      ⏱ {m}:{s}
    </span>
  );
}

export function Scorebar({ players, meId }: { players: PublicPlayer[]; meId?: string | null }) {
  return (
    <div className="scorebar" aria-label="Scores">
      {players.map((p) => (
        <div key={p.id} className={`p ${p.id === meId ? 'me' : ''} ${p.isJudge ? 'judge' : ''} ${p.done ? 'done' : ''}`}>
          <Avatar player={p} />
          <span className="name">
            {p.isJudge ? '👑 ' : ''}
            {p.name}
          </span>
          <span className="score">{p.score}</span>
        </div>
      ))}
    </div>
  );
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} · CompleteMasti` : 'CompleteMasti';
  }, [title]);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function roomLink(code: string): string {
  return `${window.location.origin}/game/${code}`;
}

export function whatsappLink(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}
